import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  H4,
  P,
  Flex,
  ProgressBar,
  Span,
  Anchor,
  PendingIcon,
  ApprovedIcon,
  DeniedIcon,
  ExpireIcon,
  InfoIcon,
} from '../../lib/components'
import { calculatePercentage, convertNumberWithCommas } from '../../lib/utils/helpers'
import { UserInvestmentDetails } from '../../lib/types'
import { RootState } from '../../store'
import ExtendLimitModal from '../../lib/components/ExtendLimitModal/extend-limit-modal'
import { usePendingInvestmentLimitRequest } from '../../lib/utils/use-pending-investment-limit-request'

interface DashboardInfoSectionProps {
  kycStatus: string | undefined
  email: string
  name: string
  userInvestmentDetail: UserInvestmentDetails | undefined
}

const DormancyInfoIcon = () => {
  return (
    <div className='dormant-info-icon hover-container ml-8' data-testid='dormant-info-icon'>
      <InfoIcon />
      <div className='dormant-info-icon info-popup'>
        Your account is considered dormant due to any one of the following reasons:
        <ul className='ml-24 basic'>
          <li>
            You did not make any investments for <strong>6 months</strong>, so it was marked dormant
            automatically.
          </li>
          <li>
            You made an initial investment but later redeemed your entire portfolio. As a result,
            with no further activity in your account for <strong>6 months</strong>, it has been
            marked as dormant.
          </li>
        </ul>
      </div>
    </div>
  )
}

const DashboardInfoSection = ({
  kycStatus,
  email,
  name,
  userInvestmentDetail,
}: DashboardInfoSectionProps) => {
  const { accountStatus } = useSelector((state: RootState) => state.investor)
  const [showExtendLimitModal, setShowExtendLimitModal] = useState(false)
  const { hasPendingRequest, markPending, openIfAllowed } = usePendingInvestmentLimitRequest()
  const getFirstName = (name: string) => {
    return name.split(' ')[0]
  }
  const investmentPercentage =
    userInvestmentDetail?.totalInvestment &&
    userInvestmentDetail?.investmentLimit &&
    calculatePercentage(
      userInvestmentDetail?.totalInvestment,
      userInvestmentDetail?.investmentLimit,
    )
  const nearingInvestmentLimit = investmentPercentage && investmentPercentage > 90
  const investmentLimitReached = investmentPercentage && investmentPercentage >= 100
  const navigate = useNavigate()

  return (
    <div className='dashboard-top-section-wrapper'>
      <div
        className='dashboard-top-section-container-new'
        data-testid='dashboard-top-section-container'
      >
        {(email || name) && <H4 text={`Hello ${name?.length > 0 ? getFirstName(name) : email}`} />}
        <div className='dashboard-top-section-container-new-sections'>
          <div className='dashboard-top-section-left-new'>
            <Flex variant='space-between' classes={['investment-container']}>
              <Span classes={['investment-limit']} variant='bold' text='Investment Limit' />
              <span className='investment-limit-values'>
                <strong>{`$${
                  userInvestmentDetail && userInvestmentDetail.totalInvestment
                    ? convertNumberWithCommas(parseInt(userInvestmentDetail.totalInvestment) / 100)
                    : '0'
                }`}</strong>
                {`/$${
                  userInvestmentDetail && userInvestmentDetail.investmentLimit
                    ? convertNumberWithCommas(parseInt(userInvestmentDetail?.investmentLimit) / 100)
                    : '0'
                }`}
              </span>
            </Flex>
            <ProgressBar
              variant={investmentLimitReached ? 'red-bar' : 'default-bar'}
              maxValue={'100'}
              value={
                userInvestmentDetail &&
                userInvestmentDetail?.totalInvestment &&
                userInvestmentDetail?.investmentLimit
                  ? Math.ceil(
                      calculatePercentage(
                        userInvestmentDetail?.totalInvestment.toString(),
                        userInvestmentDetail?.investmentLimit.toString(),
                      ),
                    ).toString()
                  : '0'
              }
            />
            {(!!userInvestmentDetail || nearingInvestmentLimit) && (
              <>
                <Span
                  classes={['mt-16']}
                  variant='thin'
                  text={
                    investmentLimitReached
                      ? 'Your investment limit has been reached.'
                      : nearingInvestmentLimit
                      ? 'Your investment limit is soon to be reached.'
                      : ''
                  }
                />
                {hasPendingRequest ? (
                  <span
                    className='mt-4 investment-limit-cta investment-limit-cta--pending'
                    data-testid='limit-extension-pending'
                  >
                    Request under review
                  </span>
                ) : (
                  <span
                    className={'mt-4 basic-anchor investment-limit-cta'}
                    onClick={() => void openIfAllowed(() => setShowExtendLimitModal(true))}
                  >
                    Place a request to increase the Investment Limit
                  </span>
                )}
              </>
            )}
          </div>

          <div className='dashboard-top-section-right-new'>
            <div className='dashboard-top-section-right-new-status-container'>
              <div className='dashboard-top-section-right-new-status-container-kyc-header'>
                <H4 text={'KYC'} />
                {accountStatus === 'DORMANT' && <DormancyInfoIcon />}
              </div>
              <span
                className={`kyc-badge-${kycStatus} kyc-status-container-status-${
                  kycStatus === 'RESUBMIT_SOF' ? 'RESUBMIT' : kycStatus
                }`}
                data-testid='dashboard-kyc-status-value'
              >
                {accountStatus === 'DORMANT' ? (
                  <>
                    <PendingIcon height='21' width='21' /> Account Dormant
                  </>
                ) : (
                  <>
                    {kycStatus === 'NOT_STARTED' && (
                      <>
                        <PendingIcon height='21' width='21' /> Not Started
                      </>
                    )}
                    {kycStatus === 'INITIATED' && (
                      <>
                        <PendingIcon height='21' width='21' /> Initiated
                      </>
                    )}
                    {kycStatus === 'PENDING' && (
                      <>
                        <PendingIcon height='18' width='20' /> Sent For Approval
                      </>
                    )}
                    {kycStatus === 'REJECTED' && (
                      <>
                        <DeniedIcon height='18' width='20' /> Rejected
                      </>
                    )}
                    {kycStatus === 'RESUBMIT' && (
                      <>
                        <DeniedIcon height='18' width='20' /> Resubmit
                      </>
                    )}
                    {kycStatus === 'RESUBMIT_SOF' && (
                      <>
                        <DeniedIcon height='18' width='20' /> Resubmit Source of Funds
                      </>
                    )}
                    {kycStatus === 'APPROVED' && (
                      <>
                        <ApprovedIcon height='18' width='20' /> Approved
                      </>
                    )}
                    {kycStatus === 'SENT_FOR_APPROVAL' && (
                      <>
                        <PendingIcon height='21' width='21' /> Sent For Approval
                      </>
                    )}
                    {kycStatus === 'EXPIRED' && (
                      <>
                        <ExpireIcon height='22' width='22' /> Expired
                      </>
                    )}
                  </>
                )}
              </span>
            </div>
            <div
              className='dashboard-top-section-right-new-content-container'
              data-testid='dashboard-kyc-status-text'
            >
              {accountStatus === 'DORMANT' ? (
                <div>
                  <P
                    text={
                      <>
                        Your account is dormant. Please contact{' '}
                        <Anchor
                          variant='basic-anchor'
                          text={'Credibila Support'}
                          href='/contact-us'
                        />{' '}
                        to unblock your account first. Once your account is active, you can proceed
                        with the KYC process.
                      </>
                    }
                  />
                </div>
              ) : (
                <>
                  {(kycStatus === 'NOT_STARTED' || kycStatus === 'INITIATED') && (
                    <div>
                      <P
                        text={
                          <>
                            Your KYC is not completed. Please complete the KYC for investing in the
                            opportunities. &nbsp;
                            <Anchor
                              variant='basic-anchor'
                              text={kycStatus === 'NOT_STARTED' ? 'Start KYC' : 'Continue KYC'}
                              href='/profile/kyc'
                            />
                            .
                          </>
                        }
                      />
                    </div>
                  )}
                  {(kycStatus === 'SENT_FOR_APPROVAL' || kycStatus === 'PENDING') && (
                    <div>
                      <P
                        text={
                          <>
                            Your KYC has been submitted for approval and will be processed within
                            the next 24 hours. Once approved, you will be able to invest in the
                            &nbsp;
                            <Anchor
                              variant='basic-anchor'
                              text='opportunities'
                              href='/opportunities'
                            />
                            .
                          </>
                        }
                      />
                    </div>
                  )}
                  {kycStatus === 'REJECTED' && (
                    <div>
                      <P
                        text={
                          <>
                            Your KYC was rejected. Please complete the KYC process again by clicking
                            &nbsp;
                            <Anchor variant='basic-anchor' text='Start KYC' href='/profile/kyc' />.
                          </>
                        }
                      />
                    </div>
                  )}
                  {kycStatus === 'APPROVED' && (
                    <div>
                      <P
                        text={
                          <>
                            You can now go ahead to browse through &nbsp;
                            <Anchor
                              variant='basic-anchor'
                              text='opportunities'
                              href='/opportunities'
                            />
                            .
                          </>
                        }
                      />
                    </div>
                  )}
                  {kycStatus === 'RESUBMIT' && (
                    <div>
                      <P
                        text={
                          <>
                            Your KYC needs resubmission. Please complete the KYC process to invest
                            in the opportunities. &nbsp;
                            <Anchor
                              variant='basic-anchor'
                              text='Resubmit KYC'
                              href='/profile/kyc'
                            />
                            .
                          </>
                        }
                      />
                    </div>
                  )}
                  {kycStatus === 'RESUBMIT_SOF' && (
                    <div>
                      <P
                        text={
                          <>
                            Your source of funds documentation needs to be resubmitted. Please
                            update your documents to invest in the opportunities. &nbsp;
                            <Anchor
                              variant='basic-anchor'
                              text='Resubmit Source of Funds'
                              href='/profile/kyc'
                            />
                            .
                          </>
                        }
                      />
                    </div>
                  )}
                  {kycStatus === 'EXPIRED' && (
                    <div>
                      <P
                        text={
                          <>
                            Your KYC has expired. Please complete the KYC once again for investing
                            in opportunities. &nbsp;
                            <Anchor variant='basic-anchor' text={'Start KYC'} href='/profile/kyc' />
                            .
                          </>
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {showExtendLimitModal ? (
        <ExtendLimitModal
          onClose={() => setShowExtendLimitModal(false)}
          onSubmitted={markPending}
        />
      ) : null}
    </div>
  )
}

export { DashboardInfoSection }
